<template>
  <div class="new-input-wrapper base-field" :class="fieldClasses">
    <label v-if="label">{{ label }}</label>
    <div
      class="base-field__control new-input"
      :class="{ 'new-input-disabled': disabled || selected || readonly }"
    >
      <span class="base-field__icon sienit">{{ icon }}</span>
      <input
        class="base-field__input"
        :type="inputType"
        :inputmode="inputMode"
        :pattern="inputPattern"
        :value="displayValue"
        :placeholder="placeholder"
        :min="min"
        :disabled="disabled || selected"
        :readonly="readonly"
        @focus="openDropdown"
        @click="openDropdown"
        @blur="closeDropdown"
        @keydown="handleKeydown"
        @input="handleInput"
        @paste="handlePaste"
        @change="$emit('change', $event.target.value)"
      />
      <span v-if="disabled || readonly" class="base-field__action disabled">A</span>
      <span
        v-else-if="selected && clearable"
        class="base-field__action clear"
        @click="$emit('clear')"
      >
        F
      </span>
    </div>
    <div
      v-if="hasOptions"
      class="base-field__dropdown new-input-dropdown"
      :class="{ 'new-input-dropdown-active': isDropdownOpen }"
    >
      <ul class="base-field__options">
        <li
          v-for="option in filteredOptions"
          :key="optionKey(option)"
          class="base-field__option"
          @mousedown.prevent="selectOption(option)"
        >
          {{ optionValue(option) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "BaseField",
  props: {
    value: {
      type: [String, Number],
      default: "",
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    icon: {
      type: String,
      default: "Z",
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Array,
      default: () => [],
    },
    filterOptions: {
      type: Boolean,
      default: true,
    },
    optionLabel: {
      type: String,
      default: "",
    },
    optionValueKey: {
      type: String,
      default: "",
    },
    fieldClass: {
      type: [String, Array, Object],
      default: "",
    },
    min: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isDropdownOpen: false,
      search: "",
    };
  },
  computed: {
    fieldClasses() {
      return [
        this.fieldClass,
        {
          "base-field--hide-icon": this.hideIcon,
        },
      ];
    },
    inputType() {
      if (this.type === "number") return "number";
      return this.hasOptions ? "text" : this.type;
    },
    inputMode() {
      return this.type === "number" ? "decimal" : null;
    },
    inputPattern() {
      return this.type === "number" ? "[0-9]*[.]?[0-9]*" : null;
    },
    displayValue() {
      return this.selected || this.readonly ? this.value : this.search;
    },
    hasOptions() {
      return this.options.length > 0 && !this.readonly && !this.disabled && !this.selected;
    },
    filteredOptions() {
      if (!this.filterOptions) return this.options;

      const search = String(this.search || "").toLowerCase();
      return this.options.filter((option) =>
        String(this.optionValue(option)).toLowerCase().includes(search)
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.search = value || "";
      },
    },
  },
  methods: {
    openDropdown() {
      if (this.hasOptions) this.isDropdownOpen = true;
      this.$emit("focus");
    },
    closeDropdown() {
      setTimeout(() => {
        this.isDropdownOpen = false;
      }, 100);
    },
    handleKeydown(event) {
      if (this.type !== "number") return;

      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];

      if (
        allowedKeys.includes(event.key) ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      if (/^\d$/.test(event.key)) return;

      if (event.key === "." || event.key === ",") {
        const value = String(event.target.value || "");
        if (!value.includes(".") && !value.includes(",")) return;
      }

      event.preventDefault();
    },
    handleInput(event) {
      const value = this.normalizeInputValue(event.target.value);
      this.search = value;
      if (value !== event.target.value) event.target.value = value;
      this.$emit("input", value);
    },
    handlePaste(event) {
      if (this.type !== "number") return;

      event.preventDefault();

      const clipboard = event.clipboardData || window.clipboardData;
      const pastedValue = clipboard ? clipboard.getData("text") : "";
      const value = this.normalizeInputValue(pastedValue);

      event.target.value = value;
      this.search = value;
      this.$emit("input", value);
    },
    selectOption(option) {
      const value = this.normalizeInputValue(this.optionValue(option));
      this.search = value;
      this.isDropdownOpen = false;
      this.$emit("input", value);
      this.$emit("select", option);
      this.$emit("change", value);
    },
    optionValue(option) {
      if (this.optionValueKey && option && typeof option === "object") {
        return option[this.optionValueKey];
      }
      if (this.optionLabel && option && typeof option === "object") {
        return option[this.optionLabel];
      }
      return option;
    },
    optionKey(option) {
      if (option && typeof option === "object") {
        return option._id || this.optionValue(option);
      }
      return option;
    },
    // for number tpe only
    normalizeInputValue(value) {
      if (this.type !== "number" || value === "") {
        return value;
      }

      let normalized = String(value)
        .replace(",", ".")
        .replace(/[^0-9.]/g, "");
      const parts = normalized.split(".");

      if (parts.length > 2) {
        normalized = `${parts.shift()}.${parts.join("")}`;
      }

      if (normalized === ".") {
        return "0.";
      }

      if (this.min === null || normalized === "") {
        return normalized;
      }

      const number = Number(normalized);
      const min = Number(this.min);

      if (!Number.isFinite(number) || !Number.isFinite(min) || number >= min) {
        return normalized;
      }

      return String(this.min);
    },
  },
};
</script>

<style scoped lang="scss">
.base-field {
  margin-right: 0;
  width: 100%;

  &__control {
    margin-bottom: 0;
  }

  &__input[readonly] {
    cursor: default;
  }

  &__dropdown {
    z-index: 5;
  }

  &--hide-icon {
    .base-field__icon {
      display: none;
    }

    .base-field__input {
      padding-left: 12px;
    }
  }

  &--table-input {
    .base-field__control {
      margin: 0;
    }

    .base-field__input {
      border: none;
      border-radius: 0;
      font-size: 12px;
      min-height: 36px;
      padding: 8px 10px;
      width: 100%;

      &:focus {
        outline: 1px solid #9bbce0;
        outline-offset: -1px;
      }
    }

    .base-field__dropdown {
      max-height: 160px;
      z-index: 20;
    }

    .new-input-dropdown-active {
      height: auto;
      overflow: auto;
    }

    .base-field__option {
      font-size: 12px;
      line-height: 1.2;
    }
  }
}
</style>
